import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { 
  Plus, UserCog, Shield, Trash2, Edit, Search
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const ROLES = [
  { id: "super_admin", label: "Super Admin", color: "bg-red-100 text-red-700", desc: "Control total del sistema" },
  { id: "admin_empresa", label: "Admin Empresa", color: "bg-violet-100 text-violet-700", desc: "Gestiona su empresa" },
  { id: "gerente_rh", label: "Gerente RH", color: "bg-blue-100 text-blue-700", desc: "Gestiona módulos RH" },
  { id: "supervisor", label: "Supervisor", color: "bg-amber-100 text-amber-700", desc: "Ve su equipo, aprueba permisos" },
  { id: "empleado", label: "Empleado", color: "bg-emerald-100 text-emerald-700", desc: "Self-service" }
];

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "empleado"
  });

  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/users`, { headers, withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!editingUser && !formData.password)) {
      toast.error("Completa los campos requeridos");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.user_id}/role?role=${formData.role}`, {}, { headers, withCredentials: true });
        toast.success("Usuario actualizado");
      } else {
        await axios.post(`${API}/users`, formData, { headers, withCredentials: true });
        toast.success("Usuario creado");
      }
      
      setDialogOpen(false);
      setEditingUser(null);
      setFormData({ name: "", email: "", password: "", role: "empleado" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar usuario");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/users/${userId}`, { headers, withCredentials: true });
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: "",
      role: userToEdit.role
    });
    setDialogOpen(true);
  };

  const getRoleInfo = (roleId) => ROLES.find(r => r.id === roleId) || { label: roleId, color: "bg-slate-100" };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableRoles = isSuperAdmin ? ROLES : ROLES.filter(r => r.id !== "super_admin");

  return (
    <Layout 
      title="Gestión de Usuarios" 
      subtitle="Administra usuarios y sus permisos"
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingUser(null);
            setFormData({ name: "", email: "", password: "", role: "empleado" });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-700" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? "Editar Usuario" : "Crear Usuario"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Nombre completo"
                  disabled={!!editingUser}
                  data-testid="user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="correo@empresa.com"
                  disabled={!!editingUser}
                  data-testid="user-email-input"
                />
              </div>
              
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    data-testid="user-password-input"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Rol *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Selecciona rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="submit-user-btn">
                  {editingUser ? "Guardar Cambios" : "Crear Usuario"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Role Legend */}
      <Card className="mb-6 border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-violet-600" />
            Niveles de Acceso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-3">
            {ROLES.map(role => (
              <div key={role.id} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <Badge className={`${role.color} mb-2`}>{role.label}</Badge>
                <p className="text-xs text-slate-500">{role.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <UserCog className="w-12 h-12 mb-3 text-slate-300" />
              <p>No hay usuarios</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => {
                  const roleInfo = getRoleInfo(u.role);
                  return (
                    <TableRow key={u.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <span className="text-violet-600 font-semibold">
                                {u.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(u)}
                            disabled={u.user_id === user?.user_id}
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(u.user_id)}
                            disabled={u.user_id === user?.user_id || u.role === "super_admin"}
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default Users;
